













































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { userModule } from "@/store/modules/user";
import { appModule } from "@/store/modules/app";
import { alasanTtdMengunciHapus } from "@/utils/hapusSetelahTtd";
import RmkSvc from "@/services/Rmk";
import FormRmkSebabKematian from "./FormRmkSebabKematian.vue";
import CardModalTtd from "@/components/modal/CardModalTtd.vue";
import { emptySebabKematian } from "./rmkOptions";

/**
 * Form Sebab Kematian (MCCD) yang berdiri sendiri.
 *
 * Data tetap disimpan lewat modul RMK (`rmk` + `rmk_sebab_kematian`, satu record
 * per register) supaya tidak ada tabel/endpoint kembar, tetapi blanko Ringkasan
 * Masuk & Keluar tidak ditampilkan sama sekali di sini. Konsekuensinya:
 * - simpan memakai payload minimal (register/pasien/DPJP + sebab kematian) —
 *   field ringkasan yang sudah ada di DB tidak ikut dikirim sehingga tidak tertimpa;
 * - TTD & cetak memakai endpoint khusus yang tidak memvalidasi/menggantungkan diri
 *   pada Keadaan Keluar pasien.
 */
@Component({
  components: {
    FormRmkSebabKematian,
    CardModalTtd
  },
  inject: {
    isReadOnly: {
      from: "isReadOnlyDokumenPulang",
      default: () => () => false
    }
  }
})
export default class CardSebabKematian extends Vue {
  @Prop() register!: string;
  @Prop() rm!: string;
  @Prop({ default: false }) isActive!: boolean;

  loading = false;
  saving = false;
  deleting = false;
  model: any = null;
  modalTtd = false;
  isReadOnly!: () => boolean;

  get readOnly(): boolean {
    return this.isReadOnly();
  }

  get isSigned(): boolean {
    return !!this.model && this.model.signed_dpjp === true;
  }

  get formDisabled(): boolean {
    return this.readOnly || this.isSigned;
  }

  get stateuser() {
    return userModule.user;
  }

  get canSign(): boolean {
    return (
      !!this.model &&
      !!this.model.id &&
      this.model.dpjp_id === (this.stateuser && this.stateuser.id) &&
      !this.isSigned
    );
  }

  /** Hapus draft hanya untuk pembuatnya, selama belum ditandatangani DPJP. */
  get canDelete(): boolean {
    if (this.readOnly) return false;
    if (!this.model || !this.model.id) return false;
    const userId = this.stateuser && this.stateuser.id;
    if (this.model.creator_user_id != userId) return false;
    return !alasanTtdMengunciHapus(
      this.model.creator_user_id,
      [{ ditandatangani: this.isSigned, penandaTanganId: this.model.dpjp_id }],
      userId
    );
  }

  get namaDpjp(): string {
    const dpjp = this.model && this.model.dpjp;
    if (!dpjp) return "-";
    return (dpjp.identitas && dpjp.identitas.nama) || dpjp.nama || "-";
  }

  async mounted() {
    await this.loadExisting();
  }

  /** Record dipakai bersama tab lain yang menulis ke modul RMK — muat ulang saat tab dibuka. */
  @Watch("isActive")
  onActive(val: boolean) {
    if (val) this.loadExisting();
  }

  async loadExisting() {
    this.loading = true;
    try {
      const res = await RmkSvc.getByRegister(this.register);
      const data = res.data && res.data.data;
      this.model = data ? this.hydrate(data) : null;
    } catch {
      this.model = null;
    }
    this.loading = false;
  }

  hydrate(data: any) {
    if (!data.sebab_kematian) {
      data.sebab_kematian = emptySebabKematian();
    }
    return data;
  }

  createNew() {
    this.model = this.hydrate({
      id: null,
      register_id: this.register,
      pasien_rm: this.rm,
      dpjp_id: this.stateuser && this.stateuser.id,
      signed_dpjp: false
    });
  }

  async saveDraft() {
    this.saving = true;
    try {
      const payload = {
        register_id: this.register,
        pasien_rm: this.rm,
        dpjp_id: (this.model && this.model.dpjp_id) || (this.stateuser && this.stateuser.id),
        sebab_kematian: (this.model && this.model.sebab_kematian) || null
      };

      const res =
        this.model && this.model.id
          ? await RmkSvc.update(this.model.id, payload)
          : await RmkSvc.create(payload);

      if (res.data && res.data.status === "1") {
        // `store` bersifat get-or-create: bila record sudah ada, isian sebab
        // kematian belum ikut tersimpan — kirim ulang sebagai update.
        const saved = res.data.data;
        if (!this.model.id && saved && saved.id && !saved.sebab_kematian) {
          const res2 = await RmkSvc.update(saved.id, payload);
          this.model = this.hydrate((res2.data && res2.data.data) || saved);
        } else {
          this.model = this.hydrate(saved);
        }
        appModule.NoticeDelay("Sebab Kematian berhasil disimpan");
      }
    } catch {
      // pesan error ditangani interceptor service
    }
    this.saving = false;
  }

  openSignDialog() {
    const sebab = this.model && this.model.sebab_kematian;
    if (!sebab || !sebab.part1_a_penyakit) {
      appModule.sendErrorAlert(
        "Bagian I.a (penyakit yang langsung mengakibatkan kematian) wajib diisi sebelum tanda tangan."
      );
      return;
    }
    this.modalTtd = true;
  }

  async passTtd(password: string) {
    try {
      const res = await RmkSvc.signSebabKematian(this.model.id, { password });
      if (res.data && res.data.status === "1") {
        this.modalTtd = false;
        appModule.NoticeDelay("Sebab Kematian telah ditandatangani");
        appModule.addMsgTtd("");
        await this.loadExisting();
      } else {
        appModule.addMsgTtd(res.data.message);
        this.modalTtd = true;
      }
    } catch (err) {
      const msg = (err as any)?.response?.data?.message || "Gagal menandatangani";
      appModule.addMsgTtd(msg);
      this.modalTtd = true;
    }
  }

  async hapus() {
    if (!confirm("Yakin ingin menghapus Sebab Kematian ini?")) return;

    this.deleting = true;
    try {
      await RmkSvc.destroy(this.model.id);
      this.model = null;
      appModule.NoticeDelay("Sebab Kematian berhasil dihapus");
    } catch {
      // pesan error ditangani interceptor service
    }
    this.deleting = false;
  }

  printPdf() {
    if (this.model && this.model.id) {
      RmkSvc.printSebabKematian(this.model.id);
    }
  }
}
